import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import MultiSelect from "@/components/MultiSelect";
import { toast } from "sonner";
import { Download, Search, CalendarIcon, X, AlertTriangle, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

function DatePicker({ value, onChange, testid, placeholder = "Pick a date" }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          className="w-full h-10 flex items-center justify-between rounded-md border border-border bg-background px-3 text-sm hover:border-primary/50 transition-colors"
        >
          <span className={value ? "num" : "text-muted-foreground"}>
            {value ? format(value, "dd MMM yyyy") : placeholder}
          </span>
          <span className="flex items-center gap-1">
            {value && (
              <X
                className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground"
                onClick={(e) => { e.stopPropagation(); onChange(undefined); }}
              />
            )}
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 bg-popover" align="start">
        <Calendar mode="single" selected={value} onSelect={onChange} initialFocus />
      </PopoverContent>
    </Popover>
  );
}

export default function AdminDataDownload() {
  const [dtype, setDtype] = useState("gst");
  const [options, setOptions] = useState(null);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [rocs, setRocs] = useState([]);
  const [dateFrom, setDateFrom] = useState();
  const [dateTo, setDateTo] = useState();
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/filters/options").then((r) => setOptions(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { setStates([]); setCities([]); setDistricts([]); setRocs([]); setCount(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dtype]);

  const cityOpts = useMemo(() => {
    if (!options || dtype !== "gst") return [];
    const s = new Set();
    states.forEach((st) => (options.gst_state_cities[st] || []).forEach((c) => s.add(c)));
    return Array.from(s).sort();
  }, [options, states, dtype]);

  const distOpts = useMemo(() => {
    if (!options || dtype !== "mca") return [];
    const s = new Set();
    states.forEach((st) => (options.mca_state_districts[st] || []).forEach((d) => s.add(d)));
    return Array.from(s).sort();
  }, [options, states, dtype]);

  const buildPayload = () => ({
    data_type: dtype,
    states,
    cities: dtype === "gst" ? cities : [],
    districts: dtype === "mca" ? districts : [],
    rocs: dtype === "mca" ? rocs : [],
    date_from: dateFrom ? new Date(dateFrom).toISOString() : null,
    date_to: dateTo ? new Date(new Date(dateTo).setHours(23, 59, 59, 999)).toISOString() : null,
  });

  const doCount = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/data/count", buildPayload());
      setCount(data.count);
      toast.success(`${data.count.toLocaleString()} records match`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doDownload = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/admin/data/download`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload()),
      });
      if (!r.ok) throw new Error(`Download failed (${r.status})`);
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `${dtype}_export.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Downloaded ${fname}`);
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  if (!options) return <div className="text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="animate-fade-up">
      <div className="mb-6">
        <h1 className="font-heading font-bold text-3xl sm:text-4xl">Data export</h1>
        <p className="mt-1.5 text-sm text-muted-foreground">Filter across all uploaded data and download an Excel export.</p>
      </div>

      <Card className="p-6">
        <Tabs value={dtype} onValueChange={setDtype} className="mb-6">
          <TabsList data-testid="export-tabs">
            <TabsTrigger value="gst" data-testid="tab-export-gst">GST</TabsTrigger>
            <TabsTrigger value="mca" data-testid="tab-export-mca">MCA</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label>States</Label>
            <MultiSelect
              options={dtype === "gst" ? options.gst_states : options.mca_states}
              values={states}
              onChange={(v) => { setStates(v); if (dtype === "gst") setCities((c) => c.filter((x) => cityOpts.includes(x))); else setDistricts((d) => d.filter((x) => distOpts.includes(x))); }}
              testid="export-states"
            />
          </div>
          {dtype === "gst" ? (
            <div>
              <Label>Cities</Label>
              <MultiSelect options={cityOpts} values={cities} onChange={setCities} disabled={states.length === 0} testid="export-cities" />
            </div>
          ) : (
            <div>
              <Label>Districts</Label>
              <MultiSelect options={distOpts} values={districts} onChange={setDistricts} disabled={states.length === 0} testid="export-districts" />
            </div>
          )}
          {dtype === "mca" && (
            <div className="md:col-span-2">
              <Label>ROCs</Label>
              <MultiSelect options={options.rocs} values={rocs} onChange={setRocs} testid="export-rocs" />
            </div>
          )}
          <div>
            <Label>Upload date from</Label>
            <DatePicker value={dateFrom} onChange={setDateFrom} testid="date-from" placeholder="Any start date" />
          </div>
          <div>
            <Label>Upload date to</Label>
            <DatePicker value={dateTo} onChange={setDateTo} testid="date-to" placeholder="Any end date" />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={doCount} disabled={busy} data-testid="count-btn">
            <Search className="h-4 w-4 mr-1.5" /> Count records
          </Button>
          <Button onClick={doDownload} disabled={busy} data-testid="download-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download Excel
          </Button>
          {count !== null && (
            <div className="text-sm num text-muted-foreground" data-testid="count-result">
              {count.toLocaleString()} record(s) match
            </div>
          )}
        </div>
      </Card>

      {/* Danger Zone: wipe demo data */}
      <Card className="mt-8 p-6 border-destructive/40 bg-destructive/5" data-testid="danger-zone">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-md bg-destructive/10 text-destructive grid place-items-center shrink-0">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-semibold text-lg">Danger zone</div>
            <p className="text-sm text-muted-foreground mt-1">
              Wipe uploaded files, all GST/MCA records and every non-admin customer account. Useful for clearing demo/test data before going live. Admin account is preserved.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <CleanupButton
                scope="data"
                label="Clear all uploads & records"
                testid="cleanup-data"
              />
              <CleanupButton
                scope="customers"
                label="Delete all customers"
                testid="cleanup-customers"
              />
              <CleanupButton
                scope="everything"
                label="Wipe everything"
                testid="cleanup-all"
              />
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

function CleanupButton({ scope, label, testid }) {
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup", { scope, confirm });
      const d = data.deleted || {};
      toast.success(`Wiped: ${d.files || 0} files · ${(d.gst_records || 0) + (d.mca_records || 0)} records · ${d.users || 0} customers`);
      setOpen(false);
      setConfirm("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setConfirm(""); }}>
      <AlertDialogTrigger asChild>
        <Button variant="destructive" size="sm" data-testid={testid}>
          <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-popover">
        <AlertDialogHeader>
          <AlertDialogTitle>{label}?</AlertDialogTitle>
          <AlertDialogDescription>
            This action is irreversible. To proceed, type <span className="font-mono font-semibold">DELETE ALL</span> below.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Input
          placeholder="Type DELETE ALL"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid={`${testid}-input`}
        />
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            disabled={busy || confirm !== "DELETE ALL"}
            onClick={run}
            data-testid={`${testid}-confirm`}
          >
            {busy ? "Wiping..." : "Yes, wipe"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
